import { parseDate } from 'lib/date';
import type { RetrievalMode } from 'services/api/clientPortalSystems';

import {
  ActionStatus,
  toGroup,
  WorkflowAction,
  WorkflowActionOptions,
  WorkflowGroupOptions,
  WorkflowInputDiff,
} from './WorkflowAction';
import WorkflowActionModelFactory from './WorkflowActionModelFactory';

// Re-export the action model + its types so `models/Workflow` stays the one import surface for
// the workflow domain (WorkflowAction now lives in its own file, per the per-kind action models).
export * from './WorkflowAction';

// Lifecycle status of the whole run (design §11). Kept as a string union so an
// unknown future status from the API degrades gracefully rather than throwing.
export enum WorkflowStatus {
  RUNNING = 'running',
  ACTION_REQUIRED = 'action_required',
  COMPLETED = 'completed',
  CANCELED = 'canceled',
}

// `outcome` is a free-form string the engine never interprets (design §11);
// referral intake writes accepted / rejected, undecided is "none".
export const NO_OUTCOME = 'none';

// The packet value name (mirrors the API's Workflow::ATTACHMENTS_VALUE_NAME): replacing
// the packet is a change to this value, which is how it enters the impact derivation.
export const ATTACHMENTS_VALUE_NAME = 'attachments';

// The portal's own name for the patient, seeded when the run is created (the FHIR trigger or
// the sweep's spawner). Snake_case verbatim — it's a value name, not a field key.
export const PORTAL_PATIENT_NAME_VALUE = 'portal_patient_name';

// The retrieval kind's api class basename (WorkflowAction#kind) — mirrors the tasks surface's
// ACTION_KINDS.retrieve, restated here because the model can't reach into components.
const RETRIEVE_ACTION_KIND = 'RetrievePortalInformation';

// Statuses a person can meaningfully restart — the step ran, is running, or is parked on
// a person. Pending/flagged steps never started (nothing to restart, and run_action on
// them would churn instances for no reason); canceled/skipped won't run again.
const RESTARTABLE_STATUSES = new Set<ActionStatus>([
  ActionStatus.COMPLETED,
  ActionStatus.ACTION_REQUIRED,
  ActionStatus.RUNNING,
  ActionStatus.ERRORED,
]);

// A reporting name this run resolved to (OLIO-3158), keyed by the workflow value whose Attr it
// belongs to. Distinct from the raw `values` above: several raw wordings share one reporting name,
// and the list column and its filter both work on the reporting name.
export interface ResolvedAttrValueOptions {
  workflowValueName: string;
  displayName: string;
}

export type ResolvedAttrValue = ResolvedAttrValueOptions;

export interface WorkflowCategoryOptions {
  key: string;
  name: string;
  position: number;
}

// One entry of the definition's value catalog (OLIO-3022): every value name this workflow type
// declares, whether or not the run holds one yet. The by-property surfaces render fields that are
// still absent — a value with no `values[]` row — so the type and label have to arrive from the
// contract rather than from the run's data.
export interface WorkflowValueCatalogEntry {
  name: string;
  type: string | null;
  label: string | null;
}

export interface WorkflowDefinitionOptions {
  apiName: string;
  displayName: string;
  categories: WorkflowCategoryOptions[];
  valueCatalog: Partial<WorkflowValueCatalogEntry>[];
}

export interface WorkflowValueOptions {
  name: string;
  value: string | null;
  source: string;
  groupId: string | null;
  // The coverage this value belongs to (design 008 §3e) — null for a referral-level value and
  // for every value on a referral whose type declares no dimension. A coverage-scoped value
  // also has a null groupId, which is exactly why reads need the scope below rather than
  // treating "no building" as "shared".
  facetId: string | null;
  // The catalog display label (design P1-5); null when the API hasn't supplied one.
  label: string | null;
  createdByName?: string | null;
  updatedAt?: string | Date | null;
}

// One member of a fan-out dimension the referral is examining — an insurance coverage today
// (design 008 §3e). Empty until the referral's type declares a dimension, so every referral
// in flight today has none.
export interface WorkflowFacetOptions {
  id: string;
  label: string | null;
  position: number;
  dimension: string;
  archived: boolean;
}

// Which lane a read belongs to. A value belongs to a building, or to a coverage, or to
// neither — never to both — so callers pass at most one. A WorkflowAction satisfies this
// shape as-is, which is how most callers pass it.
export interface WorkflowScope {
  groupId?: string | null;
  facetId?: string | null;
}

// The current packet (the attachments behind the attachment_ids value), so the edit
// screen can show what's attached before replacing it.
export interface WorkflowAttachmentOptions {
  id: string;
  filename: string | null;
  docType: string | null;
}

// An entry in a client-configurable option list (workflow_lookups, OLIO-2728): the
// apiName slug is what gets stored on outputs; displayName is what renders. Archived
// entries label history but are never offered for new picks.
export interface WorkflowLookupOption {
  apiName: string;
  displayName: string;
  category?: string | null;
  archived?: boolean;
  // Per-key extras the list uses to declare its own behaviour, so the FE doesn't hardcode slugs
  // (OLIO-2736): a not-selected reason that owes a competitor, and a competitor that can't be
  // reported on and so asks for its name in free text.
  requiresCompetitor?: boolean;
  requiresDetail?: boolean;
}

// The wire shape: an array of {key, options} (not a key-indexed map) so the lookup-key
// strings stay snake_case — the middleware camelCases hash keys.
export interface WorkflowLookupOptions {
  key: string;
  options: Partial<WorkflowLookupOption>[];
}

// The queue record this run's Selected outcome created (OLIO-2666) — feeds the outcome
// row's "View Patient in Queue" link. locationEpisodeId/groupId can be null even when the
// node is present; archived means the episode was later archived (retire the link).
export interface WorkflowCreatedPatientOptions {
  patientId: string;
  episodeId: string;
  locationEpisodeId: string | null;
  groupId: string | null;
  archived: boolean;
}

export interface WorkflowOptions {
  id: string;
  status: WorkflowStatus;
  outcome: string;
  // How portal work runs for this (client, source_system) — manual | extension |
  // headless. Serialized for BOTH portal kinds (portal_automation and portal_discovery,
  // OLIO-2888): one mode knob covers a portal's retrievals and its sweeps.
  retrievalMode?: RetrievalMode;
  // When a person archived the run (OLIO-2599). Always implies `canceled`, so read the pair:
  // canceled + archivedAt is Archived, canceled alone is the engine ending it on its own.
  archivedAt: string | Date | null;
  createdAt: string | Date | null;
  // When the run FINISHED. Null while it is still going, and a different sentence from
  // createdAt — which is when the work was asked for. The scan card's "successfully scanned on
  // …" is the completion, so it must never silently fall back to the request time.
  completedAt?: string | Date | null;
  // Who created the run — the header's "by <name>"; null for machine-created runs.
  createdByName?: string | null;
  createdPatient?: WorkflowCreatedPatientOptions | null;
  definition: Partial<WorkflowDefinitionOptions>;
  actions: Partial<WorkflowActionOptions>[];
  values: Partial<WorkflowValueOptions>[];
  attrValues: Partial<ResolvedAttrValueOptions>[];
  groups: Partial<WorkflowGroupOptions>[];
  facets: Partial<WorkflowFacetOptions>[];
  attachments: Partial<WorkflowAttachmentOptions>[];
  lookups: Partial<WorkflowLookupOptions>[];
  // Whether this referral's patient can be pushed to an EHR, and where (OLIO-2846). Served by the
  // API rather than derived here: resolving it needs the client's integration type (spelled `matrix`
  // there, `matrixcare` at the platform) and the group -> location -> external_id chain, neither of
  // which the FE has. Absent on older payloads, so every reader must treat it as optional.
  ehrSync?: Partial<EhrSyncOptions>;
}

// `platform` is null when the client has no MatrixCare/PCC integration — the button is hidden then.
// `facilityIds` are the buildings that actually carry a vendor facility id (their own External ID or
// their Location's); a referral won for a building outside this list cannot be synced.
export interface EhrSyncOptions {
  platform: string | null;
  facilityIds: string[];
}

function getDefaults(): WorkflowOptions {
  return {
    id: '',
    status: WorkflowStatus.RUNNING,
    outcome: NO_OUTCOME,
    archivedAt: null,
    createdAt: null,
    completedAt: null,
    createdPatient: null,
    definition: {},
    actions: [],
    values: [],
    attrValues: [],
    groups: [],
    facets: [],
    attachments: [],
    lookups: [],
  };
}

export class WorkflowValue {
  name: string;
  value: string | null;
  source: string;
  groupId: string | null;
  facetId: string | null;
  label: string | null;
  // Who last set this value and when (OLIO-2846). A value can outlive the action that produced it —
  // a corrected selected_facility_id on a decided referral is not the outcome action's doing — so the
  // value carries its own attribution. null for a machine write (extraction, integration callback),
  // which is honest: there is no person to name.
  createdByName: string | null;
  updatedAt: Date | null;

  constructor(opts: Partial<WorkflowValueOptions> = {}) {
    this.name = opts.name ?? '';
    this.value = opts.value ?? null;
    this.source = opts.source ?? '';
    this.groupId = opts.groupId ?? null;
    this.facetId = opts.facetId ?? null;
    this.label = opts.label ?? null;
    this.createdByName = opts.createdByName ?? null;
    this.updatedAt = parseDate(opts.updatedAt) ?? null;
  }
}

export class WorkflowDefinition {
  apiName: string;
  displayName: string;
  categories: WorkflowCategoryOptions[];
  valueCatalog: WorkflowValueCatalogEntry[];

  constructor(opts: Partial<WorkflowDefinitionOptions> = {}) {
    this.apiName = opts.apiName ?? '';
    this.displayName = opts.displayName ?? '';
    this.categories = [...(opts.categories ?? [])].sort((a, b) => a.position - b.position);
    // Empty on a payload from before the catalog shipped, and on the list payload — every read
    // is a fallback rung, never an assertion that a name is declared.
    this.valueCatalog = (opts.valueCatalog ?? []).map((entry) => ({
      name: entry.name ?? '',
      type: entry.type ?? null,
      label: entry.label ?? null,
    }));
  }

  catalogEntry(name: string): WorkflowValueCatalogEntry | undefined {
    return this.valueCatalog.find((entry) => entry.name === name);
  }
}

// Render an input value for the staleness diff: packet values are arrays of
// `{id, filename}` objects, so show their filenames; scalar values stringify. Returns
// null for an absent value so the diff can render an em dash.
function formatInputValue(value: unknown): string | null {
  if (value == null) return null;
  if (Array.isArray(value)) {
    const formatted = value
      .map((entry) =>
        entry && typeof entry === 'object' && 'filename' in entry
          ? String((entry as { filename: string | null }).filename ?? '')
          : String(entry)
      )
      .filter((part) => part.length > 0)
      .join(', ');
    return formatted.length > 0 ? formatted : null;
  }
  return String(value);
}

export default class Workflow {
  id: string;
  status: WorkflowStatus;
  outcome: string;
  retrievalMode?: RetrievalMode;
  archivedAt: Date | null;
  createdAt: Date | null;
  completedAt: Date | null;
  createdByName: string | null;
  createdPatient: WorkflowCreatedPatientOptions | null;
  definition: WorkflowDefinition;
  actions: WorkflowAction[];
  values: WorkflowValue[];
  attrValues: ResolvedAttrValue[];
  groups: WorkflowGroupOptions[];
  facets: WorkflowFacetOptions[];
  attachments: WorkflowAttachmentOptions[];
  lookups: Record<string, WorkflowLookupOption[]>;
  ehrSync: EhrSyncOptions;

  constructor(options: Partial<WorkflowOptions> = {}) {
    const opts = { ...getDefaults(), ...options };
    this.id = opts.id;
    this.status = opts.status;
    this.outcome = opts.outcome;
    this.retrievalMode = opts.retrievalMode;
    this.archivedAt = parseDate(opts.archivedAt) ?? null;
    this.createdAt = parseDate(opts.createdAt) ?? null;
    this.completedAt = parseDate(opts.completedAt) ?? null;
    this.createdByName = opts.createdByName ?? null;
    this.createdPatient = opts.createdPatient ?? null;
    this.definition = new WorkflowDefinition(opts.definition);
    // Normalized rather than stored raw so callers never branch on absent-vs-empty: no platform and
    // no syncable buildings is exactly the "cannot sync" state an older payload represents anyway.
    this.ehrSync = {
      platform: opts.ehrSync?.platform ?? null,
      facilityIds: opts.ehrSync?.facilityIds ?? [],
    };
    // The API pre-sorts actions by (category position, entry position); keep that order so the
    // checklist reads the same every time (design §12). The factory picks each action's model
    // subclass by kind (e.g. DenialCheckAction), so per-kind behavior comes for free.
    this.actions = opts.actions.map((a) => WorkflowActionModelFactory.get(a));
    this.values = opts.values.map((v) => new WorkflowValue(v));
    this.attrValues = (opts.attrValues ?? []).map((v) => ({
      workflowValueName: v.workflowValueName ?? '',
      displayName: v.displayName ?? '',
    }));
    this.groups = opts.groups.map(toGroup);
    this.facets = opts.facets.map((f) => ({
      id: f.id ?? '',
      label: f.label ?? null,
      position: f.position ?? 0,
      dimension: f.dimension ?? '',
      archived: f.archived ?? false,
    }));
    this.attachments = opts.attachments.map((a) => ({
      id: a.id ?? '',
      filename: a.filename ?? null,
      docType: a.docType ?? null,
    }));
    this.lookups = Object.fromEntries(
      (opts.lookups ?? []).map((lookup) => [
        lookup.key ?? '',
        (lookup.options ?? []).map((item) => ({
          apiName: item.apiName ?? '',
          displayName: item.displayName ?? '',
          category: item.category ?? null,
          archived: item.archived ?? false,
          requiresCompetitor: item.requiresCompetitor ?? false,
          requiresDetail: item.requiresDetail ?? false,
        })),
      ])
    );
  }

  // Active options for pickers — archived entries label history but aren't offered.
  lookupOptions(key: string): WorkflowLookupOption[] {
    return (this.lookups[key] ?? []).filter((option) => !option.archived);
  }

  // Label for a stored slug — searches archived entries too, so history keeps rendering.
  lookupLabel(key: string, apiName: string | undefined): string | undefined {
    if (!apiName) return undefined;
    return (this.lookups[key] ?? []).find((option) => option.apiName === apiName)?.displayName;
  }

  // Cache surgery for the unread-note icon: flip one action's flag without a refetch.
  // Invalidating workflowQueryKeys.show is the obvious move and the wrong one — load_workflow is
  // 8+ queries and re-renders the whole run (groups, definition, every action's inputs/outputs
  // blobs, attachment blobs) to change one boolean.
  withUnreadActivities(actionId: string): Workflow {
    return this.withActivityFlag(actionId, true);
  }

  // The clearing half: the reader has now been shown every note on this action, so the server's
  // stamp and this flag agree again. Required rather than cosmetic — useWorkflow stops polling
  // once a run is terminal, so without this the icon would never clear on a completed referral.
  withReadActivities(actionId: string): Workflow {
    return this.withActivityFlag(actionId, false);
  }

  // Rebuilds only the affected action — WorkflowAction's constructor is pure field copies with
  // no date parsing, so an instance round-trips through the factory cleanly. Workflow's own
  // constructor does NOT (it runs parseDate on createdAt), so the run is shallow-cloned with its
  // prototype preserved rather than reconstructed.
  private withActivityFlag(actionId: string, hasNewActivities: boolean): Workflow {
    const index = this.actions.findIndex((a) => a.id === actionId);
    // Same reference when nothing changes, so React Query re-renders nothing.
    if (index === -1 || this.actions[index].hasNewActivities === hasNewActivities) return this;

    const actions = [...this.actions];
    actions[index] = WorkflowActionModelFactory.get({ ...actions[index], hasNewActivities });
    return Object.assign(Object.create(Object.getPrototypeOf(this)) as Workflow, this, { actions });
  }

  // Terminal runs are frozen except the outcome (design §11) — used to stop polling.
  get isTerminal(): boolean {
    return this.status === WorkflowStatus.COMPLETED || this.status === WorkflowStatus.CANCELED;
  }

  // A person filed this run away, as opposed to the engine canceling it on its own (a confirmed
  // duplicate). Both are `canceled`; only this one is Archived.
  get isArchived(): boolean {
    return this.archivedAt !== null;
  }

  get isCompleted(): boolean {
    return this.status === WorkflowStatus.COMPLETED;
  }

  get hasOutcome(): boolean {
    return !!this.outcome && this.outcome !== NO_OUTCOME;
  }

  // The name a portal run carries BEFORE extraction produces the demographic pair — seeded by
  // the trigger or the sweep that spawned the run. Often absent (it's compacted out when the
  // portal didn't state one), so it's a rung in the chain rather than a replacement for it.
  get portalPatientName(): string {
    return this.valueFor(PORTAL_PATIENT_NAME_VALUE)?.value?.trim() ?? '';
  }

  // The name a capture awaiting review holds, off the current retrieval action. A third place
  // a name lives: staged demographics a headless run (or sweep) collected for a person to
  // confirm — exactly the state a task card represents, and the state the committed pair
  // can't cover (it exists only after extraction or after the person confirms).
  get stagedPatientName(): string {
    return this.actions.find((action) => action.kind === RETRIEVE_ACTION_KIND)?.stagedCaptureName ?? '';
  }

  // The reporting name this run resolved to for a given workflow value, or undefined when nothing
  // has resolved yet. The list's Referral Source column reads this rather than valueFor, so it
  // shows the same name the filter matches on instead of the wording the referral captured.
  resolvedAttrValue(workflowValueName: string): string | undefined {
    return this.attrValues.find((v) => v.workflowValueName === workflowValueName)?.displayName || undefined;
  }

  // No dedicated patient-name value; compose it from the demographic values, then the current
  // retrieval's staged capture, then the portal's own seeded name, falling back to a neutral
  // label before any data exists. Committed outranks staged: a person's confirmed answer beats
  // a machine's unconfirmed capture.
  //
  // The staged and portal rungs are load-bearing for the Tasks list: on a portal_automation run
  // the first/last pair doesn't exist until extraction completes or a person confirms — both
  // downstream of every card the list shows. Real portals' events normally carry no name
  // (OLIO-3072), so for headless captures the staged rung is usually the only one with data;
  // without it every capture awaiting review titles 'New referral'.
  get patientName(): string {
    const first = this.valueFor('patient_first_name')?.value ?? '';
    const last = this.valueFor('patient_last_name')?.value ?? '';
    return `${first} ${last}`.trim() || this.stagedPatientName || this.portalPatientName || 'New referral';
  }

  get groupNames(): string[] {
    return this.groups.map((g) => g.name).filter(Boolean);
  }

  // Coarse progress for the list page: completed vs. total expected actions.
  get progress(): { completed: number; total: number } {
    return {
      completed: this.actions.filter((a) => a.isCompleted).length,
      total: this.actions.length,
    };
  }

  // Whether patientName is real data (vs. its pre-data fallback label) — key off this
  // rather than comparing against the fallback copy. The staged rung counts: a staged name is
  // real data about who this is, unconfirmed or not — a surface branching on "do we know the
  // patient" must not claim ignorance of a name it would render.
  get hasPatientName(): boolean {
    const first = this.valueFor('patient_first_name')?.value ?? '';
    const last = this.valueFor('patient_last_name')?.value ?? '';
    return Boolean(`${first} ${last}`.trim() || this.stagedPatientName);
  }

  // The winning value for a name, resolved lane-contextually like the API does (design §5,
  // design 008 §3e). Three rules:
  //
  //   1. Given a coverage — that coverage's own row, else the referral-level row. Never a
  //      sibling coverage's.
  //   2. Given a building — that building's own row, else the referral-level row. Never a
  //      coverage's.
  //   3. Given neither — the referral-level row, or nothing at all.
  //
  // Rule 3's "or nothing" is load-bearing and used to be a bug. A coverage-scoped value has a
  // null groupId, so a resolver that only knows about buildings can't tell it apart from the
  // shared row; the old fallback here handed back whichever row happened to come first and
  // presented it as the shared answer — one coverage's benefits under another coverage's payer.
  //
  // Falling back to the referral-level row in rules 1 and 2 is deliberate: a referral that
  // started before its dimension existed holds its values at referral level, and that fallback
  // is what keeps it rendering instead of going blank.
  valueFor(name: string, scope: WorkflowScope = {}): WorkflowValue | undefined {
    const named = this.values.filter((v) => v.name === name);
    const referralLevel = named.find((v) => v.groupId === null && v.facetId === null);
    if (scope.facetId) return named.find((v) => v.facetId === scope.facetId) ?? referralLevel;
    if (scope.groupId) return named.find((v) => v.groupId === scope.groupId) ?? referralLevel;
    return referralLevel;
  }

  // Resolve "current" through `valueFor` (not a fresh name->value map) so the diff
  // agrees with the pill shown elsewhere when a name is duplicated across
  // workflow-level + group-scoped values (P1-1 contract §2).
  inputDiffFor(action: WorkflowAction): WorkflowInputDiff[] {
    return (
      action.inputs
        .map((input) => ({
          name: input.name,
          ranWithRaw: input.value as unknown,
          currentRaw: (this.valueFor(input.name, action)?.value ?? null) as unknown,
        }))
        // Deep-compare (object/array values aren't reference-equal), so an identical
        // packet doesn't read as changed.
        .filter((d) => JSON.stringify(d.ranWithRaw ?? null) !== JSON.stringify(d.currentRaw ?? null))
        .map((d) => ({
          name: d.name,
          ranWith: formatInputValue(d.ranWithRaw),
          current: formatInputValue(d.currentRaw),
        }))
    );
  }

  // The transitive impact of changing these value names (design §9) — the consent list
  // for the correction cascade. Walks the declared contracts the API already serves
  // (invalidatedBy/produces per action): an action whose ANSWER depends on an affected name is
  // impacted, its produced names become affected, repeat to a fixed point. Every action's contract
  // propagates the ripple (a pending step still passes it along), but only steps that
  // actually started are worth restarting, so the returned list is filtered to those.
  // `facetIds` narrows the result to one coverage's lane (plus anything run-level downstream of it).
  // Without it, a change to a per-coverage name matches EVERY coverage's steps, because the walk is
  // by value name and names are shared across coverages — so correcting one coverage would ask to
  // re-run all of them.
  impactedActionsBy(changedNames: string[], facetIds?: string[]): WorkflowAction[] {
    const affected = new Set(changedNames);
    const impactedIds = new Set<string>();
    let grew = true;
    while (grew) {
      grew = false;
      this.actions.forEach((action) => {
        // `invalidatedBy`, NOT `required`: a step can wait on a value without its answer depending
        // on that value, and restarting it would throw away work the change cannot have affected.
        // Correcting a surname reaches DuplicatePatientSearch, which IS invalidated by the name, so
        // it restarts and its output joins `affected`. The clinical assessment waits on that output
        // but is invalidated only by the document packet, so the ripple stops there instead of
        // re-running an assessment the AI never read the name for (QA, OLIO-2613).
        //
        // Propagation is gated on the same test on purpose: a step that isn't restarting doesn't
        // rewrite its outputs, so there is nothing downstream to ripple.
        if (!action.invalidatedBy.some((name) => affected.has(name))) return;
        if (!impactedIds.has(action.id)) {
          impactedIds.add(action.id);
          grew = true;
        }
        action.produces.forEach((name) => {
          if (!affected.has(name)) {
            affected.add(name);
            grew = true;
          }
        });
      });
    }
    return this.actions.filter(
      (a) =>
        impactedIds.has(a.id) &&
        RESTARTABLE_STATUSES.has(a.status) &&
        (!facetIds || a.facetId === null || facetIds.includes(a.facetId))
    );
  }

  // Producers of this action's required inputs whose current instance is still in flight
  // (pending or running) — the mid-redo awareness signal (design §9/§13). The person can
  // still act on this step; the notice just makes sure they do it knowingly, because an
  // input may be about to change.
  inFlightProducersFor(action: WorkflowAction): WorkflowAction[] {
    return this.actions.filter(
      (producer) =>
        producer.id !== action.id &&
        // Only RE-RUN producers warn (a first-pass producer isn't redoing anything);
        // pending still counts — a queued redo behind a running sibling is in flight.
        producer.rerun &&
        // Lane-scoped: a per-building producer only feeds its own building, and a per-coverage
        // one only feeds its own coverage (a shared producer feeds everyone) — a sibling lane's
        // redo is irrelevant.
        (producer.groupId === null || producer.groupId === action.groupId) &&
        (producer.facetId === null || producer.facetId === action.facetId) &&
        (producer.status === ActionStatus.PENDING || producer.status === ActionStatus.RUNNING) &&
        // `required`, not `invalidatedBy`, and deliberately so: this notice is about AWARENESS,
        // not correctness. Something feeding this step is being recomputed and the person is about
        // to act on it, which is worth saying even when the value cannot change this step's answer.
        // impactedActionsBy above uses invalidatedBy because it decides what to THROW AWAY.
        producer.produces.some((name) => action.required.includes(name))
    );
  }

  // Actions grouped under their category for display, in definition order. Actions
  // whose category is unknown fall into a trailing group so nothing is dropped.
  actionsByCategory(): { category: WorkflowCategoryOptions; actions: WorkflowAction[] }[] {
    return this.definition.categories.map((category) => ({
      category,
      actions: this.actions.filter((a) => a.categoryKey === category.key),
    }));
  }
}
